import { useEffect, useMemo, useState } from "react";
import { api, asArray, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Plus, Pencil, Trash2, X, Save, EyeOff, Eye, Search, AlertTriangle,
} from "lucide-react";
import { StatusBadge } from "@/components/Primitives";

// Approval-rule CRUD (sub-project I, Task 9 -- "ADDED AFTER TASK 7": Task 7
// built ticket_approval_rules and the transition gate correctly, then
// flagged that nothing in the plan ever gave the collection a screen. A
// rule could only be created by inserting a document into Mongo by hand.
// This screen is `TicketFieldSettings.jsx`'s own precedent, followed
// closely: MANAGE only (service_desk_manager / l3_technician, plus
// super_admin/msp_owner) -- the server refuses everyone else with 403, and
// the nav entry is hidden from anyone the server would refuse (Layout.jsx).
//
// Mirrors backend/authz.py's STAFF_ROLES exactly. The approver picker below
// lists STAFF users only: an approver who is client-scoped would be a
// customer signing off their own provider's change, and could never reach
// POST /tickets/{id}/approve anyway -- that router sits behind
// client_scope_guard with no PORTAL_ALLOW entry. The server independently
// refuses a client-scoped or unknown id in approver_ids (422, naming it) --
// this list is what stops a manager from ever being OFFERED one, not the
// only thing standing between one and the collection.
const STAFF_ROLES = [
  "super_admin", "msp_owner", "service_desk_manager",
  "l1_technician", "l2_technician", "l3_technician",
  "security_analyst", "compliance_officer", "auditor",
];

const inputCls =
  "w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 focus:border-zinc-600 outline-none";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-900 sticky top-0 bg-zinc-950">
          <div className="text-sm font-medium text-zinc-100">{title}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// Same shape as TicketFieldSettings.jsx's own ErrorBanner, and for the same
// reason: a toast auto-dismisses, uses aria-live="polite" (never
// role="alert"), and this app's single global <Toaster> (App.js) has no
// richColors, so an error toast is visually indistinguishable from success.
// Rendered inside the modal, where the caller is looking when the refusal
// happens.
function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div className="mb-3 border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">
      {message}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-zinc-600 mt-1">{hint}</div>}
    </div>
  );
}

// ⚠️ Saving a rule with an empty approver_ids BLOCKS EVERY TICKET in that
// category -- Task 7's runtime gate correctly fails closed on a
// misconfiguration. The API itself does not refuse an empty list (an MSP
// genuinely mid-configuration, about to add approvers next, is a real and
// legitimate state) -- so the screen is where "not silently" has to live.
// Shown inline, in words, and the Save/Create button stays disabled until
// the acknowledgement checkbox is ticked.
function EmptyApproversWarning({ acknowledged, onAcknowledge }) {
  return (
    <div className="mt-1 mb-3 border border-amber-900/50 bg-amber-950/20 text-amber-300 rounded px-3 py-2.5 text-xs space-y-2">
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} className="shrink-0 mt-0.5" />
        <span>
          No approver is selected. This rule will still be saved as ACTIVE,
          which means <span className="font-medium">every ticket in this category
          will be blocked and unable to leave "open" until an approver is added</span> --
          the engine fails closed on this on purpose, but nothing here should
          save it without you knowing that first.
        </span>
      </div>
      <label className="flex items-center gap-2 pl-6 select-none cursor-pointer">
        <input type="checkbox" checked={acknowledged} onChange={(e) => onAcknowledge(e.target.checked)} />
        I understand this blocks every ticket in this category until an approver is added.
      </label>
    </div>
  );
}

const EMPTY = { category: "", approver_ids: [], active: true };

function ApproverPicker({ staff, value, onChange }) {
  const toggle = (id) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };
  if (!staff.length) {
    return <div className="text-xs text-zinc-600">No staff users found.</div>;
  }
  return (
    <div className="max-h-44 overflow-y-auto border border-zinc-800 rounded divide-y divide-zinc-900">
      {staff.map((u) => (
        <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-900/60 cursor-pointer select-none">
          <input type="checkbox" checked={value.includes(u.id)} onChange={() => toggle(u.id)} />
          <span className="flex-1 min-w-0 truncate">{u.name}</span>
          <span className="text-[10px] uppercase tracking-wider text-zinc-600 font-mono shrink-0">
            {(u.role_label || u.role || "").toString().replaceAll("_", " ")}
          </span>
        </label>
      ))}
    </div>
  );
}

export default function ApprovalRuleSettings() {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [createAck, setCreateAck] = useState(false);
  const [editAck, setEditAck] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [editErr, setEditErr] = useState("");

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/ticket-approval-rules");
      setRows(asArray(r.data));
      setErr("");
    } catch (e) {
      // An unreachable list must not look like an empty one.
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  // Staff-only, client-side, same idiom as NewTicketDialog.jsx's own
  // CLIENT_SCOPED_ROLES mirror -- the server is the real gate (a
  // client-scoped or unknown id in approver_ids is refused 422, naming it),
  // this only keeps the picker from ever OFFERING an id that could not work.
  useEffect(() => {
    api.get("/users").then((r) => setStaff(
      asArray(r.data).filter((u) => !u.deleted && STAFF_ROLES.includes(u.role))
    )).catch(() => setStaff([]));
  }, []);

  const staffById = useMemo(() => Object.fromEntries(staff.map((u) => [u.id, u.name])), [staff]);
  const nameFor = (id) => staffById[id] || id;

  const shown = useMemo(() => rows.filter((r) => {
    const s = `${r.category}`.toLowerCase();
    return !q || s.includes(q.toLowerCase());
  }), [rows, q]);

  async function create() {
    setCreateErr("");
    // No <form> wraps this modal -- same reasoning as
    // TicketFieldSettings.jsx's own client-side guard: `required` on an
    // input enforces nothing here, the submit button is a plain onClick.
    if (!form.category.trim()) {
      setCreateErr("Category is required.");
      return;
    }
    if (form.approver_ids.length === 0 && !createAck) {
      setCreateErr("Check the box below to confirm, or add at least one approver.");
      return;
    }
    try {
      await api.post("/ticket-approval-rules", {
        category: form.category.trim(),
        approver_ids: form.approver_ids,
        active: form.active,
      });
      toast.success(`Created rule for '${form.category.trim()}'`);
      setCreating(false); setForm(EMPTY); setCreateAck(false); load();
    } catch (e) {
      // formatApiError, not e?.response?.data?.detail -- FastAPI's 422 body
      // is an ARRAY of objects for a validation error, and sonner renders a
      // toast title as a React child with no stringification. Kept as a
      // PERSISTENT banner (see ErrorBanner) as well as a toast, because a
      // toast alone auto-dismisses and is visually the same colour as
      // success on this app's one global, non-richColors <Toaster>.
      setCreateErr(formatApiError(e));
      toast.error(formatApiError(e));
    }
  }

  async function saveEdit() {
    setEditErr("");
    if (editing.approver_ids.length === 0 && !editAck) {
      setEditErr("Check the box below to confirm, or add at least one approver.");
      return;
    }
    try {
      const r = await api.patch(`/ticket-approval-rules/${editing.id}`, {
        approver_ids: editing.approver_ids, active: editing.active,
      });
      const n = Object.keys(r.data?.changed || {}).length;
      toast.success(n ? `Updated ${n} field${n > 1 ? "s" : ""}` : "Nothing changed");
      setEditing(null); setEditAck(false); load();
    } catch (e) {
      setEditErr(formatApiError(e));
      toast.error(formatApiError(e));
    }
  }

  async function toggleActive(r) {
    try {
      await api.patch(`/ticket-approval-rules/${r.id}`, { active: !r.active });
      toast.success(r.active ? "Deactivated" : "Reactivated");
      load();
    } catch (e) {
      setErr(formatApiError(e));
      toast.error(formatApiError(e));
    }
  }

  async function remove(r) {
    if (!window.confirm(
      `Delete the approval rule for '${r.category}'?\n\nThis is a hard delete -- refused if any ticket in that category is still pending a decision.`)) return;
    try {
      await api.delete(`/ticket-approval-rules/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      setErr(formatApiError(e));
      toast.error(formatApiError(e), { duration: 6000 });
    }
  }

  return (
    <div className="p-6" data-testid="approval-rule-settings-page">
      <div className="flex items-center gap-3 mb-5">
        <ShieldCheck size={18} className="text-zinc-400" />
        <div>
          <h1 className="text-lg font-display font-semibold text-zinc-50">Change Approval Rules</h1>
          <div className="text-xs text-zinc-500">
            Which categories require sign-off before a ticket can leave "open", and who may decide.
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-2.5 text-zinc-600" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search categories…"
              className="bg-zinc-900 border border-zinc-800 rounded pl-8 pr-3 py-1.5 text-sm text-zinc-200 outline-none focus:border-zinc-600 w-56" />
          </div>
          <button onClick={() => { setForm(EMPTY); setCreateAck(false); setCreateErr(""); setCreating(true); }}
            data-testid="approval-rule-new"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm">
            <Plus size={14} /> New rule
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-4 border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">
          {err}
        </div>
      )}

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-900">
              <th className="text-left px-4 py-2.5">Category</th>
              <th className="text-left px-4 py-2.5">Approvers</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-zinc-600 text-sm">Loading…</td></tr>
            )}
            {!loading && !shown.length && !err && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-zinc-600 text-sm">
                No approval rules yet -- every category can leave "open" without a sign-off until one exists.
              </td></tr>
            )}
            {shown.map((r) => (
              <tr key={r.id} data-testid={`approval-rule-row-${r.id}`}
                className={`border-b border-zinc-900/70 hover:bg-zinc-900/40 ${!r.active ? "opacity-50" : ""}`}>
                <td className="px-4 py-2.5 font-mono text-[12.5px] text-zinc-300">{r.category}</td>
                <td className="px-4 py-2.5 text-zinc-300">
                  {r.approver_ids?.length
                    ? r.approver_ids.map(nameFor).join(", ")
                    : (
                      <span className="text-amber-400 inline-flex items-center gap-1.5" data-testid={`approval-rule-no-approvers-${r.id}`}>
                        <AlertTriangle size={12} /> None -- blocks every ticket in this category
                      </span>
                    )}
                </td>
                <td className="px-4 py-2.5">
                  <StatusBadge status={r.active ? "active" : "disabled"} />
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => { setEditErr(""); setEditAck((r.approver_ids || []).length > 0); setEditing({ ...r, approver_ids: [...(r.approver_ids || [])] }); }}
                      title="Edit" data-testid={`approval-rule-edit-${r.id}`}
                      className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => toggleActive(r)}
                      title={r.active ? "Deactivate" : "Reactivate"}
                      data-testid={`approval-rule-toggle-${r.id}`}
                      className="p-1.5 rounded hover:bg-zinc-800 text-amber-400">
                      {r.active ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button onClick={() => remove(r)} title="Delete"
                      data-testid={`approval-rule-delete-${r.id}`}
                      className="p-1.5 rounded hover:bg-zinc-800 text-red-400">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <Modal title="New approval rule" onClose={() => setCreating(false)}>
          <ErrorBanner message={createErr} />
          <Field label="Category" hint="Must match a ticket category exactly (free text, e.g. Security Alert). Cannot be changed later.">
            <input className={inputCls} value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="Security Alert" />
          </Field>
          <Field label="Approvers" hint="Staff only -- a client-scoped account can never reach the approve/reject routes.">
            <ApproverPicker staff={staff} value={form.approver_ids}
              onChange={(ids) => setForm({ ...form, approver_ids: ids })} />
          </Field>
          {form.approver_ids.length === 0 && (
            <EmptyApproversWarning acknowledged={createAck} onAcknowledge={setCreateAck} />
          )}
          <label className="flex items-center gap-2 text-sm text-zinc-300 mt-2 mb-1 select-none">
            <input type="checkbox" checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Active immediately
          </label>
          <button onClick={create} data-testid="approval-rule-create-submit"
            disabled={form.approver_ids.length === 0 && !createAck}
            className="mt-3 w-full py-2 rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm flex items-center justify-center gap-2">
            <Save size={14} /> Create rule
          </button>
        </Modal>
      )}

      {editing && (
        <Modal title={`Edit '${editing.category}'`} onClose={() => setEditing(null)}>
          <ErrorBanner message={editErr} />
          <Field label="Category" hint="Not editable -- rule_for looks a rule up by category. Deactivate and create a new one to change it.">
            <input className={inputCls} value={editing.category} disabled
              style={{ opacity: 0.6, cursor: "not-allowed" }} />
          </Field>
          <Field label="Approvers" hint="Staff only.">
            <ApproverPicker staff={staff} value={editing.approver_ids}
              onChange={(ids) => setEditing({ ...editing, approver_ids: ids })} />
          </Field>
          {editing.approver_ids.length === 0 && (
            <EmptyApproversWarning acknowledged={editAck} onAcknowledge={setEditAck} />
          )}
          <div className="flex items-center justify-between py-2 border-t border-zinc-900 mt-2">
            <div>
              <div className="text-sm text-zinc-200">Active</div>
              <div className="text-[11px] text-zinc-600">Off = this category is not gated. Reactivating restores the same rule.</div>
            </div>
            <button onClick={() => setEditing({ ...editing, active: !editing.active })}
              data-testid="approval-rule-edit-active-toggle"
              className={`rounded-full transition-colors relative shrink-0 ${editing.active ? "bg-emerald-500/70" : "bg-zinc-700"}`}
              style={{ height: 22, width: 40 }}>
              <span className={`absolute top-0.5 w-[18px] h-[18px] rounded-full bg-white transition-all ${editing.active ? "left-[20px]" : "left-0.5"}`} />
            </button>
          </div>
          <button onClick={saveEdit} data-testid="approval-rule-edit-submit"
            disabled={editing.approver_ids.length === 0 && !editAck}
            className="mt-3 w-full py-2 rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm flex items-center justify-center gap-2">
            <Save size={14} /> Save changes
          </button>
        </Modal>
      )}
    </div>
  );
}
